import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { BackofficeHostnameDefaults } from '@nava/backoffice-bff-contracts';

export function EditHostnameDefaultsModal({
  defaults,
  onSave,
  onClose,
}: {
  defaults: BackofficeHostnameDefaults;
  onSave: (value: { insights: string; customer_success: string }) => Promise<unknown>;
  onClose: () => void;
}) {
  const [values, setValues] = useState({
    insights: defaults.insights.value,
    customer_success: defaults.customer_success.value,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  const dirty =
    values.insights !== defaults.insights.value ||
    values.customer_success !== defaults.customer_success.value;
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(values);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to update defaults');
    } finally {
      setSaving(false);
    }
  }
  return (
    <dialog
      ref={dialog}
      aria-labelledby="hostname-defaults-title"
      className="fixed inset-0 m-auto w-[calc(100%_-_2rem)] max-w-lg rounded-xl border border-neutral-200 bg-white p-6 shadow-xl backdrop:bg-neutral-950/40"
      onCancel={(event) => {
        event.preventDefault();
        if (!saving) onClose();
      }}
    >
      <form className="space-y-5" onSubmit={submit}>
        <h2 id="hostname-defaults-title" className="text-title font-semibold">
          Default interview hostnames
        </h2>
        <p className="text-sm text-neutral-600">
          These defaults apply to new studies. Existing studies keep their selected hostname.
        </p>
        {(['insights', 'customer_success'] as const).map((line) => (
          <div key={line}>
            <label htmlFor={`hostname-default-${line}`} className="block text-sm font-semibold">
              {line === 'insights' ? 'Insights' : 'Customer Success'}
            </label>
            <select
              id={`hostname-default-${line}`}
              value={values[line]}
              disabled={saving}
              onChange={(event) => {
                setValues({ ...values, [line]: event.target.value });
                setError(null);
              }}
              className="focus-ring mt-2 w-full rounded-lg border border-neutral-200 bg-white p-2"
            >
              {defaults[line].options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.hostname}
                </option>
              ))}
            </select>
          </div>
        ))}
        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            className="focus-ring rounded-lg border border-neutral-200 px-4 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!dirty || saving}
            className="focus-ring rounded-lg bg-backoffice-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
